import { ContainerButton } from "_lib";
import { Form, Input, Typography } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useCountdown, useCustomerManagementStore } from "_hooks";
import { StyledAppVerifyFlex, StyledAppVerifyCodeForm } from "./index.styled";
import { CustomerManagementOTPPurposeEnum } from "_constants";
import dayjs from "dayjs";

const AppVerifyCode = (props) => {
  const { payload, next, loading = false, onVerify = null } = props;

  const {
    customerManagement: {
      verifyOTPStatus,
      emailVerificationStatus,
      phoneVerificationStatus,
      initiateForgotPasswordStatus,
    },
    clearVerifyOTPStatus,
    verifyOTP,
    emailVerification,
    phoneVerification,
    clearEmailVerificationStatus,
    clearPhoneVerificationStatus,
    initiateForgotPassword,
    clearInitiateForgotPasswordStatus,
  } = useCustomerManagementStore();

  const [sendAgainTime, setSendAgainTime] = useState(dayjs().add(1, "minutes"));

  const sendAgainCountdown = useCountdown({
    time: sendAgainTime,
    shorten: true,
  });

  useEffect(() => {
    if (verifyOTPStatus === "success") {
      clearVerifyOTPStatus();
      next();
    }
  }, [clearVerifyOTPStatus, next, verifyOTPStatus]);

  useEffect(() => {
    if (emailVerificationStatus === "success") {
      clearEmailVerificationStatus();
    }
  }, [clearEmailVerificationStatus, emailVerificationStatus]);

  useEffect(() => {
    if (phoneVerificationStatus === "success") {
      clearPhoneVerificationStatus();
    }
  }, [clearPhoneVerificationStatus, phoneVerificationStatus]);

  useEffect(() => {
    if (initiateForgotPasswordStatus === "success") {
      clearInitiateForgotPasswordStatus();
    }
  }, [clearInitiateForgotPasswordStatus, initiateForgotPasswordStatus]);

  const handleEmailOTPResend = useCallback(() => {
    if (payload?.purpose === CustomerManagementOTPPurposeEnum.ForgotPassword) {
      initiateForgotPassword({ email: payload?.email });
    } else {
      emailVerification({ email: payload?.email });
    }
  }, [
    emailVerification,
    initiateForgotPassword,
    payload?.email,
    payload?.purpose,
  ]);

  const onClickResend = useCallback(() => {
    if (payload?.email) {
      handleEmailOTPResend();
    }
    if (payload?.phoneNumber) {
      phoneVerification({
        phoneNumber: payload?.phoneNumber,
      });
    }
    setSendAgainTime(dayjs().add(1, "minutes"));
  }, [
    handleEmailOTPResend,
    payload?.email,
    payload?.phoneNumber,
    phoneVerification,
  ]);

  const onFinish = useCallback(
    (values) => {
      const finalPayload = { ...values, channel: "WEB", ...payload };
      if (onVerify && typeof onVerify === "function") {
        onVerify(finalPayload);
      } else {
        verifyOTP(finalPayload);
      }
    },
    [onVerify, payload, verifyOTP]
  );

  const resendButtonTitle = useMemo(() => {
    let title = "Resend Code";
    if (sendAgainCountdown) {
      title += ` in ${sendAgainCountdown}`;
    }
    return title;
  }, [sendAgainCountdown]);

  const verifyLoading =
    verifyOTPStatus === "loading" ||
    emailVerificationStatus === "loading" ||
    phoneVerificationStatus === "loading" ||
    loading;

  const sendAgainLoading =
    initiateForgotPasswordStatus === "loading" ||
    phoneVerificationStatus === "loading";

  return (
    <>
      <StyledAppVerifyCodeForm onFinish={onFinish}>
        <Form.Item
          name="verificationCode"
          rules={[
            {
              required: true,
              message: "Please input your verification code",
            },
          ]}
        >
          <Input.OTP type="number" />
        </Form.Item>
        <ContainerButton
          size="large"
          title="Next"
          type="primary"
          fullWidth
          loading={verifyLoading}
          htmlType="submit"
        />
      </StyledAppVerifyCodeForm>
      <StyledAppVerifyFlex align="center" justify="center">
        <Text>Didn't receive anything? </Text>
        <ContainerButton
          type="link"
          title={resendButtonTitle}
          onClick={onClickResend}
          disabled={verifyLoading || !!sendAgainCountdown}
          loading={sendAgainLoading}
        />
      </StyledAppVerifyFlex>
    </>
  );
};

const { Text } = Typography;

AppVerifyCode.propTypes = {
  next: PropTypes.func.isRequired,
  payload: PropTypes.objectOf(
    PropTypes.shape({
      email: PropTypes.string,
      phoneNumber: PropTypes.string,
      purpose: PropTypes.string,
    })
  ).isRequired,
  loading: PropTypes.bool,
  onVerify: PropTypes.func,
};

export default AppVerifyCode;
